import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import type {
  CalendarPeriod,
  CreateSeriesPayload,
  CreateSlotPayload,
  UpdateSeriesPayload,
  UpdateSlotPayload,
  ValidateUpdateSlotPayload,
} from 'app/typings/core/extranet/inventory/calendar';
import type { CalendarSeriesScope } from 'app/typings/core/extranet/inventory/enums/CalendarSeriesScope';
import { FREQUENCY_MODE } from 'app/typings/core/extranet/inventory/enums/FrequencyMode';

import type { AvailabilityFormValues } from '../_components/AvailabilityForm/availabilityForm.types';

import { JS_DAY_TO_DAY_OF_WEEK } from './openingHours';
import { isoWeekdayToJsDay } from './weekdays';

const API_TIME_FORMAT = 'HH:mm';
const API_DATE_FORMAT = 'YYYY-MM-DD';

export const isRecurring = (values: AvailabilityFormValues): boolean =>
  values.frequency !== FREQUENCY_MODE.NONE;

export const toApiTime = (time: Dayjs): string => time.format(API_TIME_FORMAT);

export const toApiDate = (date: Dayjs): string => date.format(API_DATE_FORMAT);

const toRepeatDays = (values: AvailabilityFormValues): DayOfWeek[] => {
  switch (values.frequency) {
    case FREQUENCY_MODE.DAILY:
      return Object.values(DAY_OF_WEEK);
    case FREQUENCY_MODE.WEEKLY:
      return [JS_DAY_TO_DAY_OF_WEEK[values.date.day()]];
    case FREQUENCY_MODE.CUSTOM:
      return values.weekdays.map(
        (day) => JS_DAY_TO_DAY_OF_WEEK[isoWeekdayToJsDay(day)]
      );
    default:
      return [];
  }
};

export const toCreateSlotPayload = (
  values: AvailabilityFormValues
): CreateSlotPayload => ({
  date: toApiDate(values.date),
  stock: values.capacity,
  startTime: toApiTime(values.startTime),
  endTime: toApiTime(values.endTime),
});

export const toCreateSeriesPayload = (
  values: AvailabilityFormValues
): CreateSeriesPayload => ({
  stock: values.capacity,
  startTime: toApiTime(values.startTime),
  endTime: toApiTime(values.endTime),
  startDate: toApiDate(values.date),
  repeatDays: toRepeatDays(values),
});

export const toUpdateSlotPayload = (
  values: AvailabilityFormValues
): UpdateSlotPayload => ({
  date: toApiDate(values.date),
  stock: values.capacity,
  startTime: toApiTime(values.startTime),
  endTime: toApiTime(values.endTime),
});

export const toValidateUpdateSlotPayload = (
  values: AvailabilityFormValues
): ValidateUpdateSlotPayload => ({
  ...toUpdateSlotPayload(values),
  repeatDays: toRepeatDays(values),
});

export const toUpdateSeriesPayload = (
  values: AvailabilityFormValues,
  scope: CalendarSeriesScope
): UpdateSeriesPayload => ({
  date: toApiDate(values.date),
  scope,
  stock: values.capacity,
  startTime: toApiTime(values.startTime),
  endTime: toApiTime(values.endTime),
  repeatDays: toRepeatDays(values),
});

export const toCalendarPeriod = (date: Date): CalendarPeriod => {
  const current = dayjs(date);
  const monday = current
    .subtract((current.day() + 6) % 7, 'day')
    .startOf('day');

  return {
    startDate: toApiDate(monday),
    endDate: toApiDate(monday.add(6, 'day')),
  };
};
